/**
 * Nav items editor — /settings/custom-modules/$key/nav
 *
 * Manage the sidebar entries that appear under this module. For S5c,
 * the scope is simple: a list of nav items with create/edit/delete,
 * where each item is an "entity link" pointing at an existing entity
 * in this module.
 *
 * Custom (non-entity) pages are reserved for future work and
 * intentionally not exposed in the UI yet.
 *
 * Sort order is controlled by up/down buttons on each row — the order
 * number is implicit from the row position. On save, we rewrite
 * sortOrder to match the current index.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ChevronDown, ChevronUp, Navigation, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/custom-modules/$key/nav')({
  component: NavEditorPage,
})

function NavEditorPage() {
  const { key: moduleKey } = Route.useParams()
  const queryClient = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)

  const { data: navItems, isLoading } = useQuery(
    orpc.modules.custom.nav.list.queryOptions({ input: { moduleKey } }),
  )

  const { data: entities } = useQuery(
    orpc.modules.custom.entities.list.queryOptions({ input: { moduleKey } }),
  )

  const updateMutation = useMutation(
    orpc.modules.custom.nav.update.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: orpc.modules.custom.nav.list.queryKey({ input: { moduleKey } }),
        })
        void queryClient.invalidateQueries({
          queryKey: orpc.modules.custom.nav.listForSidebar.queryKey(),
        })
      },
      onError: (err) => toast.error(err.message),
    }),
  )

  const deleteMutation = useMutation(
    orpc.modules.custom.nav.delete.mutationOptions({
      onSuccess: () => {
        toast.success('Nav item deleted')
        void queryClient.invalidateQueries({
          queryKey: orpc.modules.custom.nav.list.queryKey({ input: { moduleKey } }),
        })
        void queryClient.invalidateQueries({
          queryKey: orpc.modules.custom.nav.listForSidebar.queryKey(),
        })
      },
      onError: (err) => toast.error(err.message),
    }),
  )

  const move = (index: number, direction: -1 | 1) => {
    if (!navItems) return
    const target = index + direction
    if (target < 0 || target >= navItems.length) return
    // Swap the two sortOrders so they re-render in the new order on
    // refetch. This is the minimal amount of writes to reorder.
    const a = navItems[index]
    const b = navItems[target]
    if (!a || !b) return
    updateMutation.mutate({ id: a.id, sortOrder: b.sortOrder })
    updateMutation.mutate({ id: b.id, sortOrder: a.sortOrder })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Navigation</h2>
          <p className="text-sm text-muted-foreground">
            Customize how this module appears in the sidebar.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add nav item
        </Button>
      </div>

      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : !navItems || navItems.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Navigation className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">
              No custom nav items yet. By default, the sidebar shows the module as a single
              link — add items here to show entities as collapsible sub-entries.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-2">
            <ul className="divide-y">
              {navItems.map((item, index) => (
                <li key={item.id} className="flex items-center gap-2 py-2 px-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{item.label}</p>
                    <p className="text-xs text-muted-foreground">
                      {item.type === 'entity' && item.entityKey
                        ? `→ ${item.entityKey}`
                        : item.type === 'custom_page'
                          ? '(custom page — not rendered yet)'
                          : '(no entity selected)'}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={index === 0}
                      onClick={() => move(index, -1)}
                    >
                      <ChevronUp className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={index === navItems.length - 1}
                      onClick={() => move(index, 1)}
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditingId(item.id)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteMutation.mutate({ id: item.id })}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <NavItemDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        moduleKey={moduleKey}
        entities={entities ?? []}
      />

      {editingId && navItems && (
        <EditNavItemDialog
          open={true}
          onClose={() => setEditingId(null)}
          moduleKey={moduleKey}
          entities={entities ?? []}
          item={navItems.find((i) => i.id === editingId) ?? null}
        />
      )}
    </div>
  )
}

// ---------- Create dialog ----------

function NavItemDialog({
  open,
  onClose,
  moduleKey,
  entities,
}: {
  open: boolean
  onClose: () => void
  moduleKey: string
  entities: Array<{ id: string; key: string; name: string; namePlural: string | null }>
}) {
  const [label, setLabel] = useState('')
  const [entityKey, setEntityKey] = useState('')
  const [icon, setIcon] = useState('')
  const queryClient = useQueryClient()

  const createMutation = useMutation(
    orpc.modules.custom.nav.create.mutationOptions({
      onSuccess: () => {
        toast.success('Nav item created')
        void queryClient.invalidateQueries({
          queryKey: orpc.modules.custom.nav.list.queryKey({ input: { moduleKey } }),
        })
        void queryClient.invalidateQueries({
          queryKey: orpc.modules.custom.nav.listForSidebar.queryKey(),
        })
        setLabel('')
        setEntityKey('')
        setIcon('')
        onClose()
      },
      onError: (err) => toast.error(err.message),
    }),
  )

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add nav item</DialogTitle>
          <DialogDescription>
            Creates a sidebar entry that links to an entity's list view.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="nav-label">Label</Label>
            <Input
              id="nav-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="All audits"
              autoFocus
            />
          </div>
          <div>
            <Label htmlFor="nav-entity">Entity</Label>
            <Select value={entityKey} onValueChange={setEntityKey}>
              <SelectTrigger id="nav-entity">
                <SelectValue placeholder="Pick an entity…" />
              </SelectTrigger>
              <SelectContent>
                {entities.length === 0 ? (
                  <SelectItem value="__none" disabled>
                    No entities in this module yet
                  </SelectItem>
                ) : (
                  entities.map((e) => (
                    <SelectItem key={e.id} value={e.key}>
                      {e.namePlural ?? e.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="nav-icon">Icon (optional)</Label>
            <Input
              id="nav-icon"
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              placeholder="ClipboardList"
            />
            <p className="text-xs text-muted-foreground mt-1">Lucide icon name.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!label || !entityKey || createMutation.isPending}
            onClick={() =>
              createMutation.mutate({
                moduleKey,
                type: 'entity',
                label,
                entityKey,
                icon: icon || undefined,
              })
            }
          >
            {createMutation.isPending ? 'Adding…' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ---------- Edit dialog ----------

function EditNavItemDialog({
  open,
  onClose,
  moduleKey,
  entities,
  item,
}: {
  open: boolean
  onClose: () => void
  moduleKey: string
  entities: Array<{ id: string; key: string; name: string; namePlural: string | null }>
  item: { id: string; label: string; entityKey: string | null; icon: string | null } | null
}) {
  const [label, setLabel] = useState(item?.label ?? '')
  const [entityKey, setEntityKey] = useState(item?.entityKey ?? '')
  const [icon, setIcon] = useState(item?.icon ?? '')
  const queryClient = useQueryClient()

  const updateMutation = useMutation(
    orpc.modules.custom.nav.update.mutationOptions({
      onSuccess: () => {
        toast.success('Nav item updated')
        void queryClient.invalidateQueries({
          queryKey: orpc.modules.custom.nav.list.queryKey({ input: { moduleKey } }),
        })
        void queryClient.invalidateQueries({
          queryKey: orpc.modules.custom.nav.listForSidebar.queryKey(),
        })
        onClose()
      },
      onError: (err) => toast.error(err.message),
    }),
  )

  if (!item) return null

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit nav item</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="nav-edit-label">Label</Label>
            <Input
              id="nav-edit-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="nav-edit-entity">Entity</Label>
            <Select value={entityKey} onValueChange={setEntityKey}>
              <SelectTrigger id="nav-edit-entity">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {entities.map((e) => (
                  <SelectItem key={e.id} value={e.key}>
                    {e.namePlural ?? e.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="nav-edit-icon">Icon</Label>
            <Input
              id="nav-edit-icon"
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={updateMutation.isPending}
            onClick={() =>
              updateMutation.mutate({
                id: item.id,
                label,
                entityKey: entityKey || null,
                icon: icon || null,
              })
            }
          >
            {updateMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
